import { token } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Descriptions, Divider, Flex } from 'antd';
import styled from 'styled-components';
import { Bleed, Main, Padding, SidePanel } from '../../common/components';
import { getDatePeriodString } from '../../common/utils';
import translations from '../algorithms/KeysViewPanel.translations.json';
import { RequiredKeyDataType } from '../schema';
import { useKeyOptions } from './useKeyOptions';
import { renderClassifierPreview, renderClassifiersPreview } from './utils';

const { addTranslations, useTranslation } = i18nNsFactory('algorithms-table');
addTranslations(translations);

interface KeysViewPanelProps {
  requiredKey: RequiredKeyDataType;
  indicatorStatus?: boolean;
  onClose: () => void;
}

export function KeysViewPanel({
  requiredKey,
  indicatorStatus,
  onClose,
}: KeysViewPanelProps) {
  const { t } = useTranslation();
  const { recipientClassifiers, ruleClassifiers, statusClassifiers } =
    useKeyOptions(indicatorStatus);

  return (
    <SidePanel
      minWidth={700}
      side="right"
      width={1786}
      collapsedWidth={700}
      mode="layered"
    >
      <SidePanelWrapper>
        <Padding>
          <Flex align="end" justify="space-between">
            <h4>{t('key.heading.label')}</h4>
          </Flex>
        </Padding>
        <Divider style={{ margin: 0 }} />
        <Scrollable>
          <ScrollContent>
            <Descriptions
              column={1}
              layout="vertical"
              items={[
                {
                  key: 'key',
                  label: t('fields.key.label'),
                  children: requiredKey.key,
                },
                {
                  key: 'name',
                  label: t('fields.name.label'),
                  children: requiredKey.name,
                },
                ...(indicatorStatus
                  ? [
                      {
                        key: 'statusId',
                        label: t('fields.statusId.label'),
                        children: renderClassifierPreview(
                          requiredKey.statusId,
                          statusClassifiers
                        ),
                      },
                    ]
                  : []),
                {
                  key: 'ruleTypeId',
                  label: t('fields.ruleTypeId.label'),
                  children: renderClassifierPreview(
                    requiredKey.ruleTypeId,
                    ruleClassifiers
                  ),
                },
                {
                  key: 'validPeriod',
                  label: t('fields.validPeriod.label'),
                  children: getDatePeriodString(
                    requiredKey.validPeriod?.[0],
                    requiredKey.validPeriod?.[1]
                  ),
                },
              ]}
            />
            <Bleed direction="horizontal">
              <Divider />
            </Bleed>
            <Descriptions
              column={1}
              layout="vertical"
              items={[
                {
                  key: 'voluntary',
                  label: t('fields.voluntary.label'),
                  children: t(
                    `fields.voluntary.${requiredKey.voluntary ? 'yes' : 'no'}`
                  ),
                },
                {
                  key: 'recipientIds',
                  label: t('fields.recipientIds.label'),
                  children: renderClassifiersPreview(
                    requiredKey.recipientIds,
                    recipientClassifiers
                  ),
                },
                {
                  key: 'description',
                  label: t('fields.description.label'),
                  children: requiredKey.description,
                },
              ]}
            />
          </ScrollContent>
        </Scrollable>
        <Main.Footer>
          <FullWidthFlex justify="end" gap="small">
            <Button id="close-validationRule" onClick={() => onClose()}>
              {t('button.close')}
            </Button>
          </FullWidthFlex>
        </Main.Footer>
      </SidePanelWrapper>
    </SidePanel>
  );
}

const Scrollable = styled.div`
  overflow-y: auto;
`;
const ScrollContent = styled(Padding)`
  overflow-x: hidden;
`;

const FullWidthFlex = styled(Flex)`
  width: 100%;
`;
const SidePanelWrapper = styled.div`
  display: grid;
  grid-template-rows: auto auto 1fr 60px;
  height: 100%;
  width: 100%;
  overflow-y: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};
`;
